import axios from "axios";
import { logEvent, getClientUid } from "./logger";
import { beginEditorRequest, finishEditorRequest, auditFetch } from "./editorAudit";

// export const API_URL = "http://127.0.0.1:8000/api";
// export const API_URL = "https://api.magicboard.shop/api";
export const API_URL = "https://origin.api.magicboard.shop/api";
// export const API_URL = "https://test-server.magicboard.shop/api";


// Демо-режим: true = заказы оформляются без оплаты
export const DEMO_MODE = false;


// ─── Инстанс axios ────────────────────────────────────────────────────────────

const api = axios.create({
  baseURL: API_URL,
  timeout: 120_000, // 60с — запас для тяжёлых запросов
  withCredentials: true, // слать cookie сессии: по ней сервер находит корзину
  headers: {
    "Accept": "application/json",
  },
});

// ─── Индикатор статуса сохранения (для чипа в редакторе) ─────────────────────
const _WRITE_METHODS = new Set(["post", "put", "patch", "delete"]);
// только редакторские записи — иначе индикатор мигал бы на utm/promo/cart/orders
const _SAVE_URL_RE = /\/(books|spreads|photos|cover-photos)\//;

const _saveStatus = { state: "idle", error: null, pending: 0 };
const _saveSubs = new Set();
function _emitSave() { _saveSubs.forEach((fn) => { try { fn(); } catch (_) {} }); }

export function subscribeSaveStatus(fn) { _saveSubs.add(fn); return () => _saveSubs.delete(fn); }
export function getSaveStatus() { return { state: _saveStatus.state, error: _saveStatus.error }; }

// ─── Интерцептор ответов: нормализуем ошибки ─────────────────────────────────

// Лог старта запроса + замер времени
api.interceptors.request.use((config) => {
  config.metadata = { ...(config.metadata || {}), start: performance.now() };
  config.metadata.editorAudit = beginEditorRequest(config);
  // Токен обхода обслуживания (разработчики) — читаем напрямую, без импортов, чтобы не было цикла
  try {
    const _bp = localStorage.getItem("mb_bypass");
    if (_bp) { config.headers = config.headers || {}; config.headers["X-Maintenance-Bypass"] = _bp; }
  } catch (e) {}
  const method = (config.method || "get").toUpperCase();
  logEvent({
    source: "network",
    event: "http_request",
    message: `${method} ${config.baseURL || ""}${config.url}`,
    url: `${config.baseURL || ""}${config.url}`,
  });

  // Индикатор сохранения: считаем незавершённые редакторские write-запросы
  config.metadata.isWrite =
    _WRITE_METHODS.has((config.method || "get").toLowerCase()) &&
    _SAVE_URL_RE.test(config.url || "");
  if (config.metadata.isWrite) {
    _saveStatus.pending += 1;
    _saveStatus.state = "saving";
    _saveStatus.error = null;
    _emitSave();
  }

  return config;
});

api.interceptors.response.use(
  (response) => {
    finishEditorRequest(response.config, response, false);
    const ms = response.config?.metadata
      ? Math.round(performance.now() - response.config.metadata.start)
      : null;
    const method = (response.config?.method || "get").toUpperCase();
    logEvent({
      source: "network",
      event: "http_response",
      message: `${method} ${response.config?.url} → ${response.status}${ms != null ? ` (${ms}ms)` : ""}`,
      url: response.config?.url,
      payload: { status: response.status, ms },
    });

    if (response.config?.metadata?.isWrite) {
      _saveStatus.pending = Math.max(0, _saveStatus.pending - 1);
      if (_saveStatus.pending === 0 && _saveStatus.state !== "error") {
        _saveStatus.state = "idle";
      }
      _emitSave();
    }

    return response;
  },
  (error) => {
    finishEditorRequest(error.config, error.response, true);
    // Режим обслуживания включили, пока клиент был на сайте → показываем баннер
    if (error?.response?.status === 503 &&
        (error.response.headers?.["x-maintenance"] === "1" ||
         error.response.data?.code === "maintenance")) {
      try { window.dispatchEvent(new Event("mb-maintenance")); } catch (e) {}
    }
    // Формируем человекочитаемое сообщение из Django REST ответа
    const data = error.response?.data;
    let message = "Неизвестная ошибка";

    if (typeof data === "string") {
      message = data;
    } else if (data?.detail) {
      message = data.detail;
    } else if (typeof data === "object" && data !== null) {
      // DRF возвращает словарь полей: { field: ["ошибка"] }
      message = Object.entries(data)
        .map(([field, errs]) => `${field}: ${Array.isArray(errs) ? errs.join(", ") : errs}`)
        .join(" | ");
    }

    const ms = error.config?.metadata
      ? Math.round(performance.now() - error.config.metadata.start)
      : null;
    const method = (error.config?.method || "get").toUpperCase();
    logEvent({
      level: "error",
      source: "network",
      event: "http_error",
      message: `${method} ${error.config?.url} → ${error.response?.status || "network error"}${ms != null ? ` (${ms}ms)` : ""}: ${message}`,
      url: error.config?.url,
      payload: { status: error.response?.status, ms, data },
    });

    if (error.config?.metadata?.isWrite) {
      _saveStatus.pending = Math.max(0, _saveStatus.pending - 1);
      _saveStatus.state = "error";
      _saveStatus.error = message;
      _emitSave();
    }

    const err = new Error(message);
    err.status = error.response?.status;
    err.code = error.code;
    err.data = data;
    err.requestId = error.config?.metadata?.editorAudit?.request_id || null;
    return Promise.reject(err);
  }
);

// ─── API ──────────────────────────────────────────────────────────────────────

export const bookApi = {

  // ── Книги ──────────────────────────────────────────────────────────────────

  /**
   * Создать новую книгу.
   * @param {{ bookSizeId, widthMm, heightMm, title?, pagePadding?, cellGap?, cellRadius? }} params
   * @returns {Promise<Book>}
   */
  async createBook({ bookSizeId, widthMm, heightMm, title, pagePadding, cellGap, cellRadius, bookFormatId, pagesCount }) {
    const { data } = await api.post("/books/", {
      book_size_id:  bookSizeId,
      width_mm:      widthMm,
      height_mm:     heightMm,
      title:         title || "Моя фотокнига",
      page_padding:  pagePadding ?? 8,
      cell_gap:      cellGap    ?? 4,
      cell_radius:   cellRadius ?? 5,
      book_format:   bookFormatId ?? null,
      pages_count:   pagesCount  ?? 0,
    });
    return data;
  },

  /**
   * Получить книгу со всеми данными (развороты + фото).
   * @returns {Promise<Book>}
   */
  async getBook(bookId) {
    const { data } = await api.get(`/books/${bookId}/`);
    return data;
  },

  async getCoverLayouts(side) {
    const params = side ? { side } : {};
    const { data } = await api.get("/cover-layouts/", { params });
    return data; // [{ id, title, tag, side, kind, slots, params }]
  },

  async setBookCoverLayouts(bookId, { frontLayoutId, backLayoutId, frontPhotoShape, backPhotoShape } = {}) {
    const body = {};
    if (frontLayoutId !== undefined) body.front_cover_layout = frontLayoutId;
    if (backLayoutId !== undefined) body.back_cover_layout = backLayoutId;
    if (frontPhotoShape !== undefined) body.front_cover_photo_shape = frontPhotoShape;
    if (backPhotoShape !== undefined) body.back_cover_photo_shape = backPhotoShape;
    const { data } = await api.patch(`/books/${bookId}/`, body);
    return data; // обновлённая книга (с *_cover_layout_data)
  },

  // Готовые шаблоны обложки (пресеты «перед + зад сразу»)
  async getCoverPresets() {
    const { data } = await api.get("/cover-presets/");
    return data; // [{ id, title, front_layout_data, back_layout_data, sticker_data, cover_bg_color, ..., preview_url }]
  },

  async applyCoverPreset(bookId, presetId) {
    const { data } = await api.post(`/books/${bookId}/cover-preset/`, {
      preset_id: presetId,
    });
    return data; // обновлённая книга (front/back_cover_layout, цвета, cover_template)
  },

  /**
   * Обновить глобальные настройки книги.
   * @param {string} bookId
   * @param {{ title?, pagePadding?, cellGap?, cellRadius? }} updates
   * @returns {Promise<Book>}
   */
  async updateBook(bookId, {
    title, pagePadding, cellGap, cellRadius, inCart,
    bookFormatId, pagesCount, bookSizeId, widthMm, heightMm,
    coverTemplateId, coverText, coverTextPosition,
    coverTitleColor, coverTitleSize, coverTitleBreak,
    coverYear, coverAccentColor, coverBgColor,
    coverDateMode, coverDateFrom, coverDateTo,
  } = {}) {
    const body = {};
    if (title              !== undefined) body.title               = title;
    if (pagePadding        !== undefined) body.page_padding        = pagePadding;
    if (cellGap            !== undefined) body.cell_gap            = cellGap;
    if (cellRadius         !== undefined) body.cell_radius         = cellRadius;
    if (inCart             !== undefined) body.in_cart             = inCart;
    if (bookFormatId       !== undefined) body.book_format         = bookFormatId;
    // pages_count намеренно НЕ отправляем: число страниц меняется только через
    // resizeBook (иначе счётчик разошёлся бы с реальным числом разворотов).
    // if (pagesCount !== undefined) body.pages_count = pagesCount;  // отключено
    if (bookSizeId         !== undefined) body.book_size_id        = bookSizeId;
    if (widthMm            !== undefined) body.width_mm            = widthMm;
    if (heightMm           !== undefined) body.height_mm           = heightMm;
    if (coverTemplateId    !== undefined) body.cover_template      = coverTemplateId;
    if (coverText          !== undefined) body.cover_text          = coverText;
    if (coverTextPosition  !== undefined) body.cover_text_position = coverTextPosition;
    if (coverTitleColor    !== undefined) body.cover_title_color   = coverTitleColor;
    if (coverTitleSize     !== undefined) body.cover_title_size    = coverTitleSize;
    if (coverTitleBreak    !== undefined) body.cover_title_break   = coverTitleBreak;
    if (coverYear          !== undefined) body.cover_year          = coverYear;
    if (coverAccentColor   !== undefined) body.cover_accent_color  = coverAccentColor;
    if (coverBgColor       !== undefined) body.cover_bg_color      = coverBgColor;
    if (coverDateMode      !== undefined) body.cover_date_mode     = coverDateMode;
    if (coverDateFrom      !== undefined) body.cover_date_from     = coverDateFrom;
    if (coverDateTo        !== undefined) body.cover_date_to       = coverDateTo;

    const { data } = await api.patch(`/books/${bookId}/`, body);
    return data;
  },

  /**
   * Удалить книгу.
   */
  async deleteBook(bookId) {
    await api.delete(`/books/${bookId}/`);
  },

  async cloneBook(bookId) {
    const { data } = await api.post(`/books/${bookId}/clone/`, {});
    return data; // данные новой книги (с новым id)
  },

  /**
   * Изменить размер книги БЕЗ сброса контента (увеличение/уменьшение).
   * Сервер сам добавит пустые развороты в конец или срежет лишние с конца
   * (с удалением их фото). Возвращает обновлённую книгу со всеми разворотами.
   * @param {string} bookId
   * @param {{ bookFormatId?, pagesCount?, bookSizeId?, widthMm?, heightMm?, targetSpreads? }} params
   * @returns {Promise<Book>}
   */
  async resizeBook(bookId, { bookFormatId, pagesCount, bookSizeId, widthMm, heightMm, targetSpreads, allowShrink, expectedSpreads, shrinkToken } = {}) {
    const body = {};
    if (bookFormatId  !== undefined) body.book_format    = bookFormatId;
    if (pagesCount    !== undefined) body.pages_count     = pagesCount;
    if (bookSizeId    !== undefined) body.book_size_id    = bookSizeId;
    if (widthMm       !== undefined) body.width_mm        = widthMm;
    if (heightMm      !== undefined) body.height_mm       = heightMm;
    if (targetSpreads !== undefined) body.target_spreads  = targetSpreads;
    if (allowShrink   !== undefined) body.allow_shrink     = allowShrink;

    if (expectedSpreads !== undefined) body.expected_spreads = expectedSpreads;
    if (shrinkToken !== undefined) body.shrink_token = shrinkToken;
    const { data } = await api.post(`/books/${bookId}/resize/`, body);
    return data;
  },

  /**
   * Изменить порядок разворотов в книге.
   * @param {string} bookId
   * @param {string[]} spreadIdsInOrder — массив UUID разворотов в новом порядке
   * @returns {Promise<Book>}
   */
  async reorderSpreads(bookId, spreadIdsInOrder) {
    const { data } = await api.post(`/books/${bookId}/spreads/reorder/`, {
      order: spreadIdsInOrder,
    });
    return data;
  },

  // ── Развороты ──────────────────────────────────────────────────────────────

  /**
   * Добавить разворот в книгу.
   * @returns {Promise<Spread>}
   */
  async addSpread(bookId, { leftLayoutId = "empty", rightLayoutId = "empty" } = {}) {
    const { data } = await api.post(`/books/${bookId}/spreads/`, {
      left_layout_id:  leftLayoutId,
      right_layout_id: rightLayoutId,
    });
    return data;
  },

  /**
   * Изменить макет разворота.
   * @returns {Promise<Spread>}
   */
  async updateSpread(spreadId, { leftLayoutId, rightLayoutId } = {}) {
    const body = {};
    if (leftLayoutId  !== undefined) body.left_layout_id  = leftLayoutId;
    if (rightLayoutId !== undefined) body.right_layout_id = rightLayoutId;

    const { data } = await api.patch(`/spreads/${spreadId}/`, body);
    return data;
  },

  /**
   * Удалить разворот.
   */
  async deleteSpread(spreadId) {
    await api.delete(`/spreads/${spreadId}/`);
  },

  // ── Фотографии ─────────────────────────────────────────────────────────────

  /**
   * Загрузить фото в слот разворота (multipart/form-data).
   * @param {string}           spreadId
   * @param {"left"|"right"}   side
   * @param {number}           slotIndex
   * @param {File}             file        — объект File из <input type="file">
   * @param {{ fit?, position?, onUploadProgress? }} opts
   * @returns {Promise<SlotPhoto>}
   */
  async uploadPhoto(spreadId, side, slotIndex, file, { fit = "cover", position = "center", layoutId, allowDuplicate, sourceHash, original, cropData, onUploadProgress, signal } = {}) {
    const fd = new FormData();
    fd.append("side",       side);
    fd.append("slot_index", slotIndex);
    // НОВАЯ ВЕРСИЯ: оригинал + координаты. Если оригинала нет (пере-сохранение
    // серверного фото) — шлём только координаты, сервер переиспользует оригинал.
    // Иначе LEGACY: обрезанный image.
    if (cropData && original) {
      fd.append("original",  original);
      fd.append("crop_data", JSON.stringify(cropData));
    } else if (cropData) {
      fd.append("crop_data", JSON.stringify(cropData));
    } else {
      fd.append("image",     file);
    }
    fd.append("fit",        fit);
    fd.append("position",   position);
    // Текущий макет стороны — по нему сервер гарантирует инвариант
    // «есть фото ⇒ непустой макет, вмещающий все фото стороны».
    if (layoutId) fd.append("layout_id", layoutId);
    // Пользователь нажал «Оставить» — сервер пропустит проверку дубля.
    if (allowDuplicate) fd.append("allow_duplicate", "true");
    if (sourceHash) fd.append("source_hash", sourceHash);

    const { data } = await api.post(`/spreads/${spreadId}/photos/`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress,
      signal,
    });
    return data;
  },

  /**
   * Изменить fit/position у уже загруженного фото.
   * @returns {Promise<SlotPhoto>}
   */
  async updatePhoto(photoId, { fit, position } = {}) {
    const body = {};
    if (fit      !== undefined) body.fit      = fit;
    if (position !== undefined) body.position = position;

    const { data } = await api.patch(`/photos/${photoId}/`, body);
    return data;
  },

  /**
   * Удалить фото из слота.
   */
  async deletePhoto(photoId) {
    await api.delete(`/photos/${photoId}/`);
  },

  // ── Настройки экспорта ─────────────────────────────────────────────────────

  /**
   * Сохранить настройки экспорта на сервере.
   * @returns {Promise<ExportSettings>}
   */
  async saveExportSettings(bookId, { quality, includeBleed, colorSpace, filename }) {
    const { data } = await api.patch(`/books/${bookId}/export-settings/`, {
      quality,
      include_bleed: includeBleed,
      color_space:   colorSpace,
      filename,
    });
    return data;
  },

  // ── Генерация PDF ──────────────────────────────────────────────────────────

  /**
   * Запрашивает PDF у сервера и инициирует скачивание через blob.
   * Поддерживает onDownloadProgress — можно показывать прогресс.
   *
   * @param {string} bookId
   * @param {{ quality, includeBleed, colorSpace, filename, onDownloadProgress? }} settings
   */
  async downloadPdf(bookId, { quality, includeBleed, colorSpace, filename, onDownloadProgress } = {}) {
    // 1. Сохраняем настройки на сервере
    await bookApi.saveExportSettings(bookId, { quality, includeBleed, colorSpace, filename });

    // 2. Запрашиваем бинарный PDF (responseType: "blob")
    const response = await api.get(`/books/${bookId}/export/`, {
      params: {
        quality,
        include_bleed: includeBleed,
        color_space:   colorSpace,
        filename,
      },
      responseType: "blob",
      timeout: 300_000, // 5 минут для больших книг
      onDownloadProgress,
    });

    // 3. Создаём временную ссылку и инициируем скачивание
    const blob = new Blob([response.data], { type: "application/pdf" });
    const url  = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href     = url;
    link.download = `${filename || "photobook"}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },

  // ── Обложки ────────────────────────────────────────────────────────────────

  async getCoverTemplates(tags = "") {
    const { data } = await api.get("/covers/", { params: tags ? { tags } : {} });
    return data;
  },

  // ── Фото обложки (задняя часть) ──────────────────────────────────────────

  /**
   * Получить список фото задней обложки.
   * @param {string} bookId
   * @returns {Promise<CoverPhoto[]>}
   */
  async getCoverPhotos(bookId) {
    const { data } = await api.get(`/books/${bookId}/cover-photos/`);
    return data;
  },

  /**
   * Загрузить фото в ячейку сетки задней обложки.
   * @param {string} bookId
   * @param {number} gridIndex  — 0–3
   * @param {File}   file
   * @param {{ signal?, onUploadProgress? }} opts
   * @returns {Promise<CoverPhoto>}
   */
  async uploadCoverPhoto(bookId, gridIndex, file, { allowDuplicate, sourceHash, original, cropData, photoShape, signal, onUploadProgress } = {}) {
    const fd = new FormData();
    fd.append("grid_index", gridIndex);
    if (photoShape !== undefined) fd.append("photo_shape", photoShape);
    if (cropData && original) {
      fd.append("original",  original);
      fd.append("crop_data", JSON.stringify(cropData));
    } else if (cropData) {
      fd.append("crop_data", JSON.stringify(cropData));
    } else {
      fd.append("image", file);
    }
    // Пользователь нажал «Оставить» — сервер пропустит проверку дубля.
    if (allowDuplicate) fd.append("allow_duplicate", "true");
    if (sourceHash) fd.append("source_hash", sourceHash);

    const { data } = await api.post(`/books/${bookId}/cover-photos/`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 120_000,  // как у загрузки фото страниц: медленная отдача с телефона
      signal,
      onUploadProgress,
    });
    return data;
  },

  async convertHeic(file, { signal } = {}) {
    // HEIC → JPEG на сервере (надёжно). Возвращает Blob image/jpeg.
    const fd = new FormData();
    fd.append("image", file);
    const { data } = await api.post(`/convert-heic/`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
      responseType: "blob",
      timeout: 120_000,
      signal,
    });
    return data;
  },

  async deleteCoverPhoto(photoId) {
    await api.delete(`/cover-photos/${photoId}/`);
  },

  // ── Корзина ────────────────────────────────────────────────────────────────

  async getCart() {
    const { data } = await api.get("/cart/");
    return data;
  },

  async addToCart(bookId, quantity = 1) {
    const { data } = await api.post("/cart/items/", { book_id: bookId, quantity });
    return data;
  },

  async removeFromCart(itemId) {
    const { data } = await api.delete(`/cart/items/${itemId}/`);
    return data;
  },

  async validateCart({ bookId, bookIds } = {}) {
    const res = await auditFetch(`${API_URL}/cart/validate/`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        book_id: bookId || null,
        book_ids: bookIds || [],
      }),
    });
    // Пытаемся прочитать тело в любом случае — сервер может вернуть
    // осмысленные problems и с не-2xx статусом.
    let data = null;
    try { data = await res.json(); } catch (e) { data = null; }
    if (!res.ok && !data) {
      const err = new Error(`HTTP ${res.status}`);
      err.status = res.status;
      throw err;
    }
    return data;
  },

  // ── Техподдержка ─────────────────────────────────────────────────────────────
  async getSupportCode() {
    let bookIds = [];
    try { bookIds = JSON.parse(localStorage.getItem("photobook_ids") || "[]"); } catch (e) {}
    const res = await fetch(`${API_URL}/support/code/`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ client_uid: getClientUid(), book_ids: bookIds }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json(); // { code, expires_at }
  },

  async restoreUser(token) {
    const res = await fetch(`${API_URL}/user/restore/?token=${encodeURIComponent(token)}`, {
      method: "GET",
      credentials: "include",
      headers: { "Accept": "application/json" },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json(); // { client_uid, book_ids }
  },

  // ── Заказы ─────────────────────────────────────────────────────────────────

  /**
   * Создать заказ.
   * @param {{ clientName, clientPhone, clientEmail?, clientBirthDate?,
   *           deliveryType, deliveryAddress?, deliveryComment?,
   *           items: [{bookId, quantity, price}] }} orderData
   */
  async createOrder({ clientName, clientLastName, clientPhone, clientEmail, clientBirthDate,
                deliveryType, deliveryAddress, deliveryComment,
                deliveryCity, deliveryPvzCode, deliveryPvzAddress,
                deliveryCityCode, deliveryPrice,
                giftToken, promoCode, items,
                consentPersonalData, consentThirdParty, consentMarketing,
                idempotencyKey }) {
    const { data } = await api.post("/orders/", {
      client_name:             clientName,
      client_last_name:        clientLastName,
      client_phone:            clientPhone,
      client_email:            clientEmail ?? "",
      client_birth_date:       clientBirthDate ?? null,
      delivery_type:           deliveryType,
      delivery_address:        deliveryAddress ?? "",
      delivery_comment:        deliveryComment ?? "",
      delivery_price:          deliveryPrice,
      delivery_city_code:      deliveryCityCode ?? null,
      delivery_city:           deliveryCity ?? "",
      delivery_pvz_code:       deliveryPvzCode ?? "",
      delivery_pvz_address:    deliveryPvzAddress ?? "",
      gift_token:              giftToken ?? "",
      promo_code:              promoCode ?? "",
      consent_personal_data:   consentPersonalData ?? false,
      consent_third_party:     consentThirdParty ?? false,
      consent_marketing:       consentMarketing ?? false,
      client_uid:              getClientUid(),
      items: items.map(i => ({ book: i.bookId, quantity: i.quantity, price: i.price })),
    }, {
      timeout: 180_000, // 3 минуты — заказ с СДЭК+Sheets+Робокассой может быть медленным
      headers: idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {},
    });
    return data;
  },

  async getOrder(orderId) {
    const { data } = await api.get(`/orders/${orderId}/`);
    return data;
  },

  /**
   * Проверить статус оплаты заказа.
   * @returns {Promise<{order_id, order_number, status, paid, total_price}>}
   */
  async checkPaymentStatus(orderId) {
    const { data } = await api.get(`/orders/${orderId}/payment-status/`);
    return data;
  },

  // ── Мои заказы (раздел «Покупки») ──────────────────────────────────────────

  /**
   * Список заказов клиента для раздела «Покупки».
   * Клиент определяется по client_uid (из localStorage) и/или сессии.
   * @returns {Promise<{ orders: Array }>}
   */
  async getMyOrders() {
    const { data } = await api.get("/orders/mine/", {
      params: { client_uid: getClientUid() },
    });
    const restored = Array.isArray(data.returned_book_ids) ? data.returned_book_ids : [];
    const saved = JSON.parse(localStorage.getItem("photobook_ids") || "[]");
    localStorage.setItem("photobook_ids", JSON.stringify([...new Set([...saved, ...restored])]));
    return data;
  },

  /**
   * Получить/сгенерировать ссылку на оплату НЕоплаченного заказа.
   * @param {string} orderId
   * @returns {Promise<{ payment_url: string, order_id: string }>}
   */
  async payOrder(orderId) {
    const { data } = await api.post(`/orders/${orderId}/pay/`, {});
    return data; // { payment_url, order_id }
  },

  /**
   * Отменить НЕоплаченный заказ. Сервер вернёт книги в корзину (in_cart=true),
   * сами книги НЕ удаляются. Оплаченный заказ отменить нельзя (сервер отдаст 409).
   * @param {string} orderId
   * @returns {Promise<{ ok: boolean, returned_books?: string[] }>}
   */
  async cancelOrder(orderId) {
    const { data } = await api.post(`/orders/${orderId}/cancel/`, {
      client_uid: getClientUid(),
    });
    const saved = JSON.parse(localStorage.getItem("photobook_ids") || "[]");
    localStorage.setItem("photobook_ids", JSON.stringify([...new Set([...saved, ...(data.returned_books || [])])]));
    if (localStorage.getItem("pending_order_id") === String(orderId)) {
      localStorage.removeItem("pending_order_id");
      localStorage.removeItem("pending_order_number");
    }
    sessionStorage.removeItem("pendingOrderHandled");
    return data;
  },

  // ── Промокоды ──────────────────────────────────────────────────────────────

  /**
   * Проверить промокод.
   * @param {{ code: string, bookFormatId?: string }} params
   * @returns {Promise<{ valid, format_ok, format_message, promo }>}
   */
  async checkPromoCode({ code, bookFormatId }) {
    const { data } = await api.post("/promo/check/", {
      code,
      book_format_id: bookFormatId ?? null,
    });
    return data;
  },

  /**
   * Проверить промокод или сертификат (единое поле).
   * @param {{ code: string, bookFormatId?: string }} params
   * @returns {Promise<{ valid, type: "promo"|"gift", ... }>}
   */
  async checkCode({ code, bookFormatId }) {
    const { data } = await api.post("/code/check/", {
      code,
      book_format_id: bookFormatId ?? null,
    });
    return data;
  },

  // ── Скидки ─────────────────────────────────────────────────────────────────

  /**
   * Проверить доступные автоматические скидки.
   * @param {{ clientPhone?: string, bookFormatIds?: string[], itemsCount?: number }} params
   * @returns {Promise<{ discounts, best_discount, prev_orders_count }>}
   */
  async checkDiscounts({ clientPhone, bookFormatIds, itemsCount }) {
    const { data } = await api.post("/discounts/check/", {
      client_phone:    clientPhone ?? "",
      book_format_ids: bookFormatIds ?? [],
      items_count:     itemsCount ?? 0,
    });
    return data;
  },

  /**
   * Поиск скидки и даты рождения по номеру телефона.
   * @param {{ phone: string, bookFormatIds?: string[], itemsCount?: number }} params
   * @returns {Promise<{ birth_date, prev_orders_count, best_discount, all_discounts }>}
   */
  async getDiscountByPhone({ phone, bookFormatIds, itemsCount }) {
    const { data } = await api.post("/discounts/by-phone/", {
      phone,
      book_format_ids: bookFormatIds ?? [],
      items_count:     itemsCount ?? 0,
    });
    return data;
  },

  // ── Форматы книг ───────────────────────────────────────────────────────────

  /**
   * Получить список видимых форматов книг (с вариантами страниц, превью, FAQ).
   * @returns {Promise<BookFormat[]>}
   */
  async getBookFormats() {
    const { data } = await api.get("/formats/");
    return data;
  },

  /**
   * Получить один формат книги по id.
   * @returns {Promise<BookFormat>}
   */
  async getBookFormat(formatId) {
    const { data } = await api.get(`/formats/${formatId}/`);
    return data;
  },

  // ── UTM-метки ──────────────────────────────────────────────────────────────

  /**
   * Отправить UTM-параметры на сервер (засчитать переход).
   * Параметры берём из window.location.search при загрузке приложения.
   * @param {{ utmSource?, utmMedium?, utmCampaign?, utmTerm?, utmContent? }} params
   */
  async trackUtm({ utmSource = "", utmMedium = "", utmCampaign = "", utmTerm = "", utmContent = "" } = {}) {
    const { data } = await api.post("/utm/track/", {
      utm_source:   utmSource,
      utm_medium:   utmMedium,
      utm_campaign: utmCampaign,
      utm_term:     utmTerm,
      utm_content:  utmContent,
    });
    return data;
  },

  // ── Аккаунты ───────────────────────────────────────────────────────────────

  /**
   * Создать аккаунт.
   * @param {{ name, phone, email?, birthDate? }} params
   */
  async createAccount({ name, phone, email, birthDate } = {}) {
    const { data } = await api.post("/accounts/", {
      name,
      phone,
      email:      email      ?? "",
      birth_date: birthDate  ?? null,
    });
    return data;
  },

  /**
   * Получить аккаунт.
   */
  async getAccount(accountId) {
    const { data } = await api.get(`/accounts/${accountId}/`);
    return data;
  },

  /**
   * Обновить аккаунт (PATCH).
   */
  async updateAccount(accountId, updates = {}) {
    const body = {};
    if (updates.name      !== undefined) body.name       = updates.name;
    if (updates.phone     !== undefined) body.phone      = updates.phone;
    if (updates.email     !== undefined) body.email      = updates.email;
    if (updates.birthDate !== undefined) body.birth_date = updates.birthDate;
    const { data } = await api.patch(`/accounts/${accountId}/`, body);
    return data;
  },

  // ── Подарки ────────────────────────────────────────────────────────────────

  /**
   * Создать подарок.
   * @param {string} accountId — id аккаунта создателя
   * @param {{ recipientName, recipientPhone, wishes?, bookFormatId }} params
   */
  async createGift(accountId, { recipientName, recipientPhone, wishes, bookFormatId }) {
    const { data } = await api.post(`/accounts/${accountId}/gifts/create/`, {
      recipient_name:  recipientName,
      recipient_phone: recipientPhone,
      wishes:          wishes ?? "",
      book_format_id:  bookFormatId,
    });
    return data;
  },

  /**
 * Создать подарок анонимно (без аккаунта).
 * @param {{ bookFormatId: string, pagesCount: number, recipientName?: string, recipientPhone?: string, wishes?: string }} params
 * @returns {Promise<Gift>}
 */
  async createGiftAnonymous({
    bookFormatId, pagesCount,
    senderName, senderPhone, senderEmail, senderBirthDate,
    recipientName, recipientPhone, wishes,
  }) {
    const { data } = await api.post("/gifts/create/", {
      book_format_id:    bookFormatId,
      pages_count:       pagesCount,
      sender_name:       senderName ?? "",
      sender_phone:      senderPhone ?? "",
      sender_email:      senderEmail ?? "",
      sender_birth_date: senderBirthDate ?? null,
      recipient_name:    recipientName ?? "",
      recipient_phone:   recipientPhone ?? "",
      wishes:            wishes ?? "",
    });
    return data;
  },

  /**
   * Получить список подарков аккаунта.
   */
  async getGifts(accountId) {
    const { data } = await api.get(`/accounts/${accountId}/gifts/`);
    return data;
  },

  /**
   * Активировать подарок по токену.
   * @param {{ token, accountId }} params
   */
  async activateGift({ token, accountId }) {
    const { data } = await api.post("/gifts/activate/", {
      token,
      account_id: accountId,
    });
    return data;
  },
};

// ─── Экспортируем инстанс для расширенного использования ─────────────────────
// Например, чтобы добавить токен авторизации снаружи:
//   import { apiInstance } from "./api";
//   apiInstance.defaults.headers.common["Authorization"] = `Token ${token}`;

export { api as apiInstance };